import React, { useState, useEffect, useCallback } from "react";
import { useStore } from "../../../../stores/root-store-context";
import { observer } from "mobx-react-lite";
import { KnowledgeBase } from "../../../../api/base/ConfigApi";
import { Typography } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import ResponsiveGrid from "../../../../../../../../../Components/TableMui/ResponsiveGrid";
import Badge from "Components/Badge/Badge";
import { useGridPagination, GridSortModel } from "../../helpers/useGridPagination";

const { Text } = Typography;

const DEFAULT_SORT: GridSortModel = [{ field: "kb_Subject", sort: "asc" }];

const PropertyFaqs: React.FC<{ companyKey: string }> = observer(({ companyKey }) => {
    const [propertyFaq, setPropertyFaq] = useState<KnowledgeBase[] | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());
    const { ricStore } = useStore();

    useEffect(() => {
        const fetchFaqs = async () => {
            try {
                setLoading(true);
                const companykey = ricStore.accountInformation?.companyKey?.toString();
                if (!companykey) {
                    setLoading(false);
                    return;
                }

                const response = await ricStore.getPropertyFaq(companykey);

                if (Array.isArray(response)) {
                    setPropertyFaq(response as KnowledgeBase[]);
                }
            } catch (error) {
                console.error("Error fetching FAQs:", error);
                setPropertyFaq([]);
            } finally {
                setLoading(false);
            }
        };

        fetchFaqs();
    }, [ricStore, companyKey]);

    const rows = React.useMemo(() => propertyFaq ?? [], [propertyFaq]);
    const { pagedRows, server } = useGridPagination(rows, { defaultSort: DEFAULT_SORT });

    const toggleExpand = useCallback((key: number) => {
        setExpandedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const columns = [
        {
            field: "kb_Category",
            label: "Category",
            flex: 0.8,
            minWidth: 120,
            render: (value: string) => <Badge text={value} variant="blue" />,
        },
        {
            field: "kb_Subject",
            label: "Subject",
            flex: 1,
            minWidth: 140,
            primary: true,
            defaultSort: "asc",
        },
        {
            field: "kb_Question",
            label: "Question/Answer",
            flex: 2.5,
            minWidth: 260,
            sortable: false,
            render: (_: unknown, record: KnowledgeBase) => {
                const isExpanded = expandedKeys.has(record.knowledgeBaseKey);
                return (
                    <div className="faq-qa">
                        <div
                            className="faq-question"
                            role="button"
                            tabIndex={0}
                            aria-expanded={isExpanded}
                            aria-controls={`faq-answer-${record.knowledgeBaseKey}`}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 8,
                                cursor: "pointer",
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleExpand(record.knowledgeBaseKey);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    toggleExpand(record.knowledgeBaseKey);
                                }
                            }}
                        >
                            <CaretRightOutlined
                                className="faq-arrow"
                                rotate={isExpanded ? 90 : 0}
                                style={{ marginTop: 4, transition: "transform 0.2s" }}
                            />
                            <Text strong>{record.kb_Question}</Text>
                        </div>

                        {isExpanded && (
                            <div
                                id={`faq-answer-${record.knowledgeBaseKey}`}
                                className="faq-answer"
                                role="region"
                                style={{ margin: "8px 0 4px 22px" }}
                            >
                                {record.kb_Answer || (
                                    <Text type="secondary">No answer provided.</Text>
                                )}
                            </div>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
        <div className="faq-container">
            <div className="ric-table">
                <ResponsiveGrid
                    columns={columns}
                    rows={pagedRows}
                    getRowId={(row: KnowledgeBase) => row.knowledgeBaseKey}
                    loading={loading}
                    server={server}
                />
            </div>
        </div>
    );
});

export default PropertyFaqs;